import {API} from "habit-core";
import {useCallback, useEffect, useRef, useState} from "react";

const IMAGE_DURATION_MS = 20000; // 20 seconds

export function useExceutionElements(
    elements: API.models.CampaignExecutionElement[] | undefined,
    enabled: boolean,
) {
    const currentExecutionElements = useRef<
        API.models.CampaignExecutionElement[] | undefined
    >();
    const newExecutionElements = useRef<
        API.models.CampaignExecutionElement[] | undefined
    >(undefined);
    const activeElementIndex = useRef<number | undefined>(undefined);
    const [activeElement, setActiveElement] = useState<
        API.models.CampaignExecutionElement | undefined
    >(undefined);
    const switchElementTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const [prevEnabled, setPrevEnabled] = useState(enabled);

    const switchElement = useCallback(() => {
        if (switchElementTimeoutRef.current) {
            // clear any existing timeout as we will create a new one once element has been switched.
            clearTimeout(switchElementTimeoutRef.current);
        }

        let nextActiveElement: API.models.CampaignExecutionElement | undefined =
            undefined;
        if (newExecutionElements.current) {
            currentExecutionElements.current = newExecutionElements.current;
            const newActiveElementIndex = newExecutionElements.current.length
                ? 0
                : undefined;
            activeElementIndex.current = newActiveElementIndex;
            nextActiveElement =
                newActiveElementIndex !== undefined
                    ? newExecutionElements.current[newActiveElementIndex]
                    : undefined;
            newExecutionElements.current = undefined;
        } else {
            let nextActiveElementIndex = (activeElementIndex.current ?? 0) + 1;
            if (
                nextActiveElementIndex >=
                (currentExecutionElements.current?.length ?? 0)
            ) {
                nextActiveElementIndex = 0;
            }
            activeElementIndex.current = nextActiveElementIndex;
            nextActiveElement =
                currentExecutionElements.current?.[nextActiveElementIndex];
        }

        if (nextActiveElement && nextActiveElement.type === "image") {
            switchElementTimeoutRef.current = setTimeout(
                switchElement,
                IMAGE_DURATION_MS,
            );
        } else if (nextActiveElement && nextActiveElement.type === "video") {
            switchElementTimeoutRef.current = setTimeout(
                switchElement,
                nextActiveElement.data.durationMs,
            );
        }
        setActiveElement(nextActiveElement);
    }, []);

    if (
        elements?.length &&
        (currentExecutionElements.current === undefined ||
            currentExecutionElements.current.length === 0) &&
        activeElement === undefined
    ) {
        newExecutionElements.current = elements;
        switchElement();
    } else if (
        currentExecutionElements.current &&
        currentExecutionElements.current.length > 0 &&
        activeElement === undefined
    ) {
        switchElement();
    } else if (
        elements !== currentExecutionElements.current &&
        elements !== newExecutionElements.current
    ) {
        newExecutionElements.current = elements;
    }

    if (prevEnabled !== enabled) {
        if (prevEnabled && !enabled && switchElementTimeoutRef.current) {
            clearTimeout(switchElementTimeoutRef.current);
        } else {
            switchElement();
        }
        setPrevEnabled(enabled);
    }

    // clears timeout on unmount if necessary.
    useEffect(() => {
        return () => {
            if (switchElementTimeoutRef.current) {
                clearTimeout(switchElementTimeoutRef.current);
            }
        };
    }, []);

    return activeElement;
}
